import React, { useEffect, useMemo, useRef, useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAvailableTableByGameId, getGameById } from "../api/game";
import { getGroupByTableAndGame, postGroup } from "../api/group";
import addIcon from "../assets/register/add-icon.svg";
import icofoto from "../assets/register/ico-foto.svg";
import removeIcon from "../assets/register/remove-icon.svg";
import { setGame, setGroup } from "../redux/actions";
import { useStore } from "react-redux";
import { getPlayablePages } from "../components/EscapeRoomRenderer";
import { ROUTES } from "../utils/constants";
import { resolveRegisterScreenPrimary } from "../utils/gameColors";
import { isRegistrationPage } from "../utils/escapeRoomConstants";
import { getGroupTable, setGroupTable, updateTimer, setLastTickNow } from "../utils/helpers";
import { alertError, alertGroup, modalTableNum } from "../utils/logger";
import { EVENELY_LOGO_URL } from "../utils/brand";
import { resolveEscapeRoomMediaUrl } from "../components/escape-room/erRendererStyleUtils";
import {
	getEffectiveRegisterConfig,
	getEffectiveNumTables,
	getEffectiveRegistrationLogoUrl,
} from "../utils/effectiveRegisterConfig";
import { resetPlaySessionToEnterCode } from "../utils/resetPlaySession";
import { compressRegisterPhoto } from "../utils/registerPhotoCompress";
import LanguageSwitcher from "../components/LanguageSwitcher";

/** URI explícita: /register?game=<ObjectId> */
function parseGameIdFromSearch(searchParams) {
	const raw = searchParams.get("game");
	if (!raw || typeof raw !== "string") return null;
	const id = raw.trim();
	return /^[a-fA-F0-9]{24}$/.test(id) ? id : null;
}

function registerIconMaskStyle(assetUrl) {
	return {
		WebkitMaskImage: `url("${assetUrl}")`,
		maskImage: `url("${assetUrl}")`,
	};
}

function getEmptyGroup() {
	return {
		fullName: "",
		game: null,
		image: "",
		tableNum: "",
		members: [],
	};
}

function resolveReturnIndexForEscapeRoom(game) {
	try {
		const raw = localStorage.getItem("erRegisterReturnIndex");
		if (raw == null) return null;
		const idx = Number(raw);
		if (!Number.isInteger(idx) || idx < 0) return null;
		const pages = getPlayablePages(game?.escapeRoom?.pages || []);
		if (idx >= pages.length) return null;
		if (isRegistrationPage(pages[idx])) return null;
		return idx;
	} catch (_) {
		return null;
	}
}

function clearRegisterReturnIndex() {
	try {
		localStorage.removeItem("erRegisterReturnIndex");
	} catch (_) {}
}

function isForceRegisterFlow() {
	try {
		return localStorage.getItem("erForceRegisterFlow") === "1";
	} catch (_) {
		return false;
	}
}

function clearForceRegisterFlow() {
	try {
		localStorage.removeItem("erForceRegisterFlow");
	} catch (_) {}
}

/** 403 al crear grupo: partida sin hueco (jugadores o mesas según plan / ER). */
function isRegisterSessionCapacityFullError(error) {
	const res = error?.response;
	if (!res || res.status !== 403) return false;
	const code = res.data?.code;
	if (code === "PLAYERS_LIMIT_EXCEEDED" || code === "SESSION_TABLES_LIMIT_EXCEEDED") return true;
	const msg = String(res.data?.message || "");
	return /máximo de jugadores|equipos\/mesas|jugadores en esta partida/i.test(msg);
}

function Register({ intl }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const t = (id, values) => intl.formatMessage({ id }, values);
	const store = useStore();
	const [searchParams] = useSearchParams();
	const gameIdFromUri = parseGameIdFromSearch(searchParams);
	const [activeGame, setActiveGame] = useState(() => {
		const g = store.getState().game;
		if (!g?._id) return null;
		if (gameIdFromUri && String(g._id) !== gameIdFromUri) return null;
		return g;
	});
	const [gameBootstrapping, setGameBootstrapping] = useState(() => {
		const g = store.getState().game;
		if (!g?._id) return true;
		if (gameIdFromUri && String(g._id) !== gameIdFromUri) return true;
		return false;
	});
	const [activeGroup, setActiveGroup] = useState(getEmptyGroup());
	const [actualMember, setActualMember] = useState("");
	const [members, setMembers] = useState([]);
	const [selectedImage, setSelectedImage] = useState(null);
	const [loading, setLoading] = useState(false);
	const registerRootRef = useRef(null);
	const registerDataHandledRef = useRef(false);
	const [capacityFullInfo, setCapacityFullInfo] = useState(null);

	const regConfig = useMemo(() => getEffectiveRegisterConfig(activeGame), [activeGame]);
	const numTablesEffective = useMemo(() => getEffectiveNumTables(activeGame), [activeGame]);

	const selectedImagePreviewUrl = useMemo(() => {
		if (!selectedImage) return "";
		return URL.createObjectURL(selectedImage);
	}, [selectedImage]);
	useEffect(() => {
		return () => {
			if (selectedImagePreviewUrl) URL.revokeObjectURL(selectedImagePreviewUrl);
		};
	}, [selectedImagePreviewUrl]);

	const noTimeScreens = [
		ROUTES.START_GAME,
		ROUTES.START_GAME2,
		ROUTES.START_GAME3,
		ROUTES.START_GAME4,
		ROUTES.START_GAME5,
		ROUTES.END,
		ROUTES.RESULTS,
		ROUTES.GAMEOVER,
	];

	useEffect(() => {
		window.scrollTo(0, 0);
		if (activeGame && !registerDataHandledRef.current) {
			registerDataHandledRef.current = true;
			handleData(activeGame);
			return;
		}
		let cancelled = false;
		const finishBootstrap = (game) => {
			if (cancelled || !game) return;
			if (!registerDataHandledRef.current) {
				registerDataHandledRef.current = true;
				handleData(game);
			}
			setGameBootstrapping(false);
		};
		if (gameIdFromUri) {
			const cached = store.getState().game;
			if (cached && String(cached._id) === gameIdFromUri) {
				finishBootstrap(cached);
				return () => { cancelled = true; };
			}
			getGameById(gameIdFromUri)
				.then((res) => {
					if (cancelled) return;
					if (res.status === 200 && res.data) {
						const gm = res.data;
						if (!gm.active) {
							setGameBootstrapping(false);
							resetPlaySessionToEnterCode(dispatch, navigate);
							return;
						}
						finishBootstrap(gm);
					} else {
						setGameBootstrapping(false);
						resetPlaySessionToEnterCode(dispatch, navigate);
					}
				})
				.catch(() => {
					if (!cancelled) {
						setGameBootstrapping(false);
						resetPlaySessionToEnterCode(dispatch, navigate);
					}
				});
			return () => { cancelled = true; };
		}
		const g = store.getState().game;
		if (g && g._id) {
			if (!g.active) {
				setGameBootstrapping(false);
				resetPlaySessionToEnterCode(dispatch, navigate);
				return;
			}
			finishBootstrap(g);
			return;
		}
		setGameBootstrapping(false);
		navigate(ROUTES.ENTER_CODE, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, navigate, gameIdFromUri]);

	function handleData(game) {
		dispatch(setGame(game));
		setActiveGame(game);
		setActiveGroup((prevGroup) => ({
			...prevGroup,
			game: game._id,
		}));
		const eff = getEffectiveRegisterConfig(game);
		const registerMode = String(eff.mode || "simple").toLowerCase();
		const isSimple = registerMode === "simple";
		let table = getGroupTable();
		const forceRegister = isForceRegisterFlow();
		if (isSimple) {
			handleGroupTableChange("");
			setGroupTable("");
		} else if (table && !forceRegister) {
			getGroupByTableAndGame(table, game._id).then((result) => {
				if (result.status === 200) reconnect(result.data.group, game);
			});
		} else {
			window.setTimeout(() => {
				if (game.demo)
					getAvailableTableByGameId(game._id)
						.then((tableRes) => {
							if (tableRes.status === 200) {
								let tableNum = tableRes.data;
								modalTableNum({ tableNum: tableNum });
								handleGroupTableChange(tableNum.toString());
							}
						})
						.catch((error) => {
							alertError({
								error: " ",
								customMessage:
									error?.response?.data?.message || " ",
							});
						});
			}, 0);
		}
	}

	function saveGroup(e) {
		e.preventDefault();
		if (loading) return;
		setLoading(true);
		const eff = getEffectiveRegisterConfig(activeGame);
		const registerMode = String(eff.mode || "simple").toLowerCase();
		const isSimple = registerMode === "simple";
		const maxPeople = Number(eff.maxPeoplePerTable ?? 0);
		const photoEnabled = eff.photoEnabled === true;
		const selectedImageEffective = photoEnabled ? selectedImage : null;

		if (!isSimple && Number.isFinite(maxPeople) && maxPeople > 0 && members.length > maxPeople) {
			alertError({ error: " ", customMessage: t("REGISTER.ERR_MAX_PEOPLE", { max: maxPeople }) });
			setLoading(false);
			return;
		}

		if (isSimple) {
			const name = (activeGroup.fullName || "").trim();
			if (!name) {
				alertError({ error: " ", customMessage: t("REGISTER.ERR_NEED_NAME") });
				setLoading(false);
				return;
			}
		} else {
			if (!members.length) {
				alertError({
					error: " ",
					customMessage: t("REGISTER.ERR_NEED_MEMBERS"),
				});
				setLoading(false);
				return;
			}
			if (!String(activeGroup.tableNum || "").trim()) {
				alertError({ error: " ", customMessage: t("REGISTER.ERR_NEED_TABLE") });
				setLoading(false);
				return;
			}
		}

		const payload = {
			...activeGroup,
			fullName: isSimple ? (activeGroup.fullName || "").trim() : activeGroup.fullName,
			members: isSimple ? [] : [...members],
			game: activeGame._id,
			tableNum: isSimple ? "" : activeGroup.tableNum,
		};

		postGroup(payload, selectedImageEffective)
			.then((res) => {
				let group = res.data;
				if (res.status === 201) {
					group.time = 0;
					if (activeGame?.escapeRoom) {
						const returnIdx = resolveReturnIndexForEscapeRoom(activeGame);
						if (returnIdx != null) group.escapeRoomPageIndex = returnIdx;
						group.lastVisited = ROUTES.ESCAPE_ROOM;
					}
					dispatch(setGroup(group));
					setGroupTable(group.tableNum);
					updateTimer(0);
					setLastTickNow();
					const dest = activeGame?.escapeRoom ? ROUTES.ESCAPE_ROOM : ROUTES.START_GAME;
					navigate(dest);
					clearRegisterReturnIndex();
					clearForceRegisterFlow();
					setLoading(false);
				}
			})
			.catch((error) => {
				if (isRegisterSessionCapacityFullError(error)) {
					setCapacityFullInfo({
						message: error?.response?.data?.message || "",
						code: error?.response?.data?.code || "",
						limit: error?.response?.data?.limit,
						current: error?.response?.data?.current,
					});
					setLoading(false);
					return;
				}
				const serverMsg =
					error?.response?.data?.message ||
					error?.response?.data?.error ||
					error?.message;
				alertError({
					error: String(error),
					customMessage: serverMsg || t("REGISTER.ERR_REGISTER"),
				});
				setLoading(false);
			});
	}

	function reconnect(group, gameCtx) {
		dispatch(setGroup(group));
		setGroupTable(group.tableNum);
		const gameRef = gameCtx || activeGame;
		const hasEscapeRoom = !!gameRef?.escapeRoom;
		if (group.lastVisited) {
			let destination = group.lastVisited;
			// Evita caer en /start-game cuando el juego actual es escape room.
			if (hasEscapeRoom && destination !== ROUTES.ESCAPE_ROOM) {
				destination = ROUTES.ESCAPE_ROOM;
			}
			if (!hasEscapeRoom && destination === ROUTES.ESCAPE_ROOM) {
				destination = ROUTES.START_GAME;
			}
			if (!noTimeScreens.includes(destination) && !group.finished)
			{
				updateTimer(Number(group.time) || 0);
				setLastTickNow();
			}
			navigate(destination);
		} else {
			if (!group.finished) {
				updateTimer(Number(group.time) || 0);
				setLastTickNow();
			}
			navigate(hasEscapeRoom ? ROUTES.ESCAPE_ROOM : ROUTES.START_GAME);
		}
		clearRegisterReturnIndex();
		clearForceRegisterFlow();
	}

	const handleAddMember = () => {
		if (
			members.length < 9 &&
			actualMember.length != 0 &&
			actualMember.length < 45 &&
			actualMember.trim().indexOf(" ") != -1
		) {
			setMembers([...members, actualMember]);
			setActualMember("");
		} else {
			let message =
				actualMember.length >= 45
					? t("REGISTER.ERR_NAME_LONG")
					: actualMember.trim().indexOf(" ") === -1
					? t("REGISTER.ERR_NAME_SURNAME")
					: t("REGISTER.ERR_MAX_MEMBERS");
			alertError({
				error: " ",
				customMessage: message,
			});
		}
	};

	const handleChange = (element) => (event) => {
		setActiveGroup({ ...activeGroup, [element]: event.target.value });
	};

	const handleGroupTableChange = (value) => {
		setActiveGroup((prevGroup) => ({
			...prevGroup,
			tableNum: value || "",
		}));
	};

	async function handleGroupTable(event) {
		getGroupByTableAndGame(event.target.value, activeGame._id)
			.then(async (resGroup) => {
				if (resGroup.status === 200) {
					await alertGroup({ data: resGroup.data }).then((res) => {
						if (res.isConfirmed) {
							reconnect(resGroup.data.group, activeGame);
						} else {
							handleGroupTableChange();
							document.getElementById("sector").selectedIndex = 0;
						}
					});
				}
			})
			.catch((error) => {
				handleGroupTableChange(event.target.value);
			});
	}

	function renderOptions() {
		return Array.from(
			{
				length: numTablesEffective,
			},
			(_, index) => (index + 1).toString()
		).map((option, index) => (
			<option key={index} value={option}>
				{option}
			</option>
		));
	}

	function renderMembers() {
		return members.map((name, index) => (
			<div
				key={index}
				role="listitem"
				className="register-member-row d-flex justify-content-between align-items-center gap-2"
			>
				<div className="register-member-name txt20">{name}</div>
				<div
					role="button"
					tabIndex={0}
					className="btn-remove register-icon-mask d-flex align-items-center justify-content-center"
					style={{ ...registerIconMaskStyle(removeIcon), width: 32, height: 32, cursor: "pointer" }}
					onClick={() =>
						setMembers(members.filter((member) => member != name))
					}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							setMembers(members.filter((member) => member != name));
						}
					}}
					aria-label={t("REGISTER.REMOVE_MEMBER")}
				/>
			</div>
		));
	}

	const registerPrimary = resolveRegisterScreenPrimary(activeGame);
	const registerBrandLogo = useMemo(() => {
		const custom = getEffectiveRegistrationLogoUrl(activeGame);
		if (custom) {
			const resolved = resolveEscapeRoomMediaUrl(custom);
			if (resolved) {
				return {
					src: resolved,
					alt: activeGame?.title || activeGame?.name || "Logo",
				};
			}
		}
		return { src: EVENELY_LOGO_URL, alt: "Evenely" };
	}, [activeGame]);
	const isSimple = String(regConfig.mode || "simple").toLowerCase() === "simple";
	const photoEnabled = regConfig.photoEnabled === true;

	if (gameBootstrapping || !activeGame) {
		return (
			<div
				className="register-page-root register-page-root--viewport-lock min-vh-100 d-flex flex-column"
				style={{ ["--register-primary"]: registerPrimary, background: "#f8fafc" }}
			>
				<main className="flex-grow-1 d-flex align-items-center justify-content-center">
					<div className="text-center" style={{ color: "#64748b", fontSize: 15 }}>{t("REGISTER.LOADING")}</div>
				</main>
			</div>
		);
	}

	if (capacityFullInfo) {
		const detail = capacityFullInfo.message || t("REGISTER.FULL_DEFAULT");
		return (
			<div
				ref={registerRootRef}
				className="register-page-root register-page-root--viewport-lock min-vh-100 d-flex flex-column"
				style={{ ["--register-primary"]: registerPrimary }}
			>
				<header className="register-page-header register-page-header--shrink px-4 px-md-5 py-4">
					<div className="register-page-header-inner d-flex align-items-center justify-content-between gap-3">
						<img
							src={registerBrandLogo.src}
							alt={registerBrandLogo.alt}
							className="register-page-brand-logo"
							onError={(e) => {
								e.currentTarget.src = "/logo.svg";
							}}
						/>
						<LanguageSwitcher />
					</div>
				</header>
				<main className="register-page-main register-page-main--scroll flex-grow-1 d-flex align-items-center justify-content-center px-3 px-md-4 pb-5">
					<div className="register-page-shell mx-auto text-center" style={{ maxWidth: 520 }}>
						<p className="register-page-eyebrow mb-2">{t("REGISTER.FULL_EYEBROW")}</p>
						<h1 className="register-page-title mb-3">{t("REGISTER.FULL_TITLE")}</h1>
						<p className="title1B txtBlue txt16 mb-4" style={{ lineHeight: 1.55 }}>
							{detail}
						</p>
						{capacityFullInfo.limit != null ? (
							<p className="title1B txtBlue txt14 mb-4" style={{ opacity: 0.85 }}>
								{t("REGISTER.FULL_LIMIT", { limit: capacityFullInfo.limit })}
								{capacityFullInfo.current != null
									? t("REGISTER.FULL_CURRENT", { current: capacityFullInfo.current })
									: ""}
							</p>
						) : null}
						<button
							type="button"
							className="btn btn-lg text-white bold border-0 px-5 py-3"
							style={{ backgroundColor: registerPrimary, borderRadius: 9999 }}
							onClick={() => {
								setCapacityFullInfo(null);
								resetPlaySessionToEnterCode(dispatch, navigate);
							}}
						>
							{t("REGISTER.BACK_TO_CODE")}
						</button>
					</div>
				</main>
			</div>
		);
	}

	return (
		<div
			ref={registerRootRef}
			className="register-page-root register-page-root--viewport-lock min-vh-100 d-flex flex-column"
			style={{ ["--register-primary"]: registerPrimary }}
		>
			{isSimple ? (
				<header className="register-page-header register-page-header--shrink register-page-header--simple px-4 py-3">
					<div className="d-flex justify-content-center align-items-center w-100 position-relative">
						<img
							src={registerBrandLogo.src}
							alt={registerBrandLogo.alt}
							className="register-page-brand-logo"
							onError={(e) => {
								e.currentTarget.src = "/logo.svg";
							}}
						/>
						<div style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}>
							<LanguageSwitcher />
						</div>
					</div>
				</header>
			) : (
				<header className="register-page-header register-page-header--shrink px-4 px-md-5 py-4">
					<div className="register-page-header-inner d-flex flex-column gap-3">
						<div className="d-flex align-items-center justify-content-between gap-3">
							<img
								src={registerBrandLogo.src}
								alt={registerBrandLogo.alt}
								className="register-page-brand-logo"
								onError={(e) => {
									e.currentTarget.src = "/logo.svg";
								}}
							/>
							<LanguageSwitcher />
						</div>
						<div className="register-page-intro text-md-end">
							<p className="register-page-eyebrow mb-1">{t("REGISTER.BEFORE_START")}</p>
							<h1 className="register-page-title mb-0">{t("REGISTER.TEAM_TITLE")}</h1>
							<p className="register-page-subtitle mb-0 mt-2">
								{t("REGISTER.TEAM_SUBTITLE")}
							</p>
						</div>
					</div>
				</header>
			)}

			<main
				className={`register-page-main register-page-main--scroll flex-grow-1 px-3 px-md-4 pb-5${
					isSimple ? " register-page-main--simple" : " register-page-main--full"
				}`}
			>
				<div className={`register-page-shell mx-auto${isSimple ? " register-page-shell--simple" : ""}`}>
					{isSimple ? (
						<>
							<div
								className={`row g-4 w-100 register-page-row register-page-row--simple${photoEnabled ? "" : " justify-content-center"}`}
							>
								<div
									className={
										photoEnabled
											? "col-12 col-md-10 col-lg-6 mx-md-auto register-page-col"
											: "col-12 register-page-col"
									}
								>
									<section className="register-panel h-100 register-panel--simple-name">
										<h2 className="register-section-title title1 mb-3 txtBlue bold txt22">{t("REGISTER.YOUR_NAME")}</h2>
										<input
											id="team-full-name"
											autoComplete="nickname"
											maxLength={24}
											value={activeGroup.fullName || ""}
											onChange={handleChange("fullName")}
											placeholder={t("REGISTER.YOUR_NAME")}
											required
											className="form-control bg-transparent txtBlue"
										/>
									</section>
								</div>
								{photoEnabled ? (
									<div className="col-12 col-md-10 col-lg-6 mx-md-auto register-page-col">
										<section className="register-panel h-100 register-panel--photo">
											<h2 className="register-section-title title1 mb-2 txtBlue bold txt22">{t("REGISTER.PHOTO_OPTIONAL")}</h2>
											<div className="fotoButton mt-2 image-upload text-center mx-auto register-foto-wrap">
												<label htmlFor="file-input-simple">
													{selectedImage ? (
														<img
															src={selectedImagePreviewUrl}
															alt={t("REGISTER.PHOTO_PREVIEW")}
															className="img-fluid icofoto-selected"
														/>
													) : (
														<div
															className="register-icon-mask register-icofoto-placeholder"
															style={registerIconMaskStyle(icofoto)}
															aria-hidden
														/>
													)}
												</label>
												<input
													id="file-input-simple"
													name="file-input-simple"
													type="file"
													accept="image/*"
													onChange={async (e) => {
														const f = e.target.files?.[0];
														if (!f) return;
														const compressed = await compressRegisterPhoto(f);
														setSelectedImage(compressed || f);
														e.target.value = "";
													}}
												/>
											</div>
										</section>
									</div>
								) : null}
							</div>
						</>
					) : (
						<>
							<div className="row g-3 g-md-4 register-page-row">
								{photoEnabled ? (
									<div className="col-12 col-lg-6 register-page-col">
										<section className="register-panel h-100 register-panel--photo">
											<div className="register-section-head">
												<p className="register-section-eyebrow">{t("REGISTER.STEP", { n: 1 })}</p>
												<h2 className="register-section-title title1 mb-3 txtBlue bold txt22">
													{t("REGISTER.TEAM_PHOTO")}
												</h2>
											</div>
											<div className="fotoButton mt-2 image-upload text-center mx-auto register-foto-wrap">
												<label htmlFor="file-input">
													{selectedImage ? (
														<img
															src={selectedImagePreviewUrl}
															alt={t("REGISTER.TEAM_PHOTO_ALT")}
															className="img-fluid icofoto-selected"
														/>
													) : (
														<div
															className="register-icon-mask register-icofoto-placeholder"
															style={registerIconMaskStyle(icofoto)}
															aria-hidden
														/>
													)}
												</label>
												<input
													id="file-input"
													name="file-input"
													type="file"
													accept="image/*"
													onChange={async (e) => {
														const f = e.target.files?.[0];
														if (!f) return;
														const compressed = await compressRegisterPhoto(f);
														setSelectedImage(compressed || f);
														e.target.value = "";
													}}
												/>
											</div>
										</section>
									</div>
								) : null}
								<div className={photoEnabled ? "col-12 col-lg-6 register-page-col" : "col-12 register-page-col"}>
									<section className="register-panel register-panel--step2">
										<div className="register-section-head">
											<p className="register-section-eyebrow">{t("REGISTER.STEP", { n: photoEnabled ? 2 : 1 })}</p>
											<h2 className="register-section-title title1 mb-3 txtBlue bold txt22">
												{t("REGISTER.ADD_PARTICIPANTS")}
											</h2>
										</div>
										<div className="container-add">
											<input
												id="member-full-name"
												value={actualMember}
												onChange={(event) => {
													setActualMember(event.target.value);
												}}
												autoComplete="off"
												placeholder={t("REGISTER.MEMBER_PLACEHOLDER")}
												required
												className="form-control input-add bg-transparent txtBlue"
											/>
											<div
												role="button"
												tabIndex={0}
												className="btn-add register-icon-mask d-flex align-items-center justify-content-center"
												style={registerIconMaskStyle(addIcon)}
												onClick={handleAddMember}
												onKeyDown={(e) => {
													if (e.key === "Enter" || e.key === " ") {
														e.preventDefault();
														handleAddMember();
													}
												}}
												aria-label={t("REGISTER.ADD_MEMBER")}
											/>
										</div>
										<div className="register-members-section mt-4">
											<div className="title1 txtBlue bold txt20 mb-2 flex-shrink-0">
												{t("REGISTER.GROUP_MEMBERS")}
											</div>
											{members?.length != 0 ? (
												<div
													className="register-members-scroll"
													role="list"
													aria-label={t("REGISTER.GROUP_MEMBERS")}
												>
													{renderMembers()}
												</div>
											) : (
												<div className="register-empty-hint txt16 mt-2">
													{t("REGISTER.EMPTY_MEMBERS")}
												</div>
											)}
										</div>
									</section>
								</div>
							</div>

							<div className="row g-3 g-md-4 mt-1 register-page-row">
								<div className="col-12 col-lg-6 register-page-col">
									<section className="register-panel h-100">
										<div className="register-section-head">
											<p className="register-section-eyebrow">{t("REGISTER.STEP", { n: photoEnabled ? 3 : 2 })}</p>
											<h2 className="register-section-title title1 mb-2 txtBlue bold txt22">
												{t("REGISTER.CHOOSE_TABLE")}
											</h2>
										</div>
										<div className="title1B txtBlue txt14 mb-3">
											{t("REGISTER.TABLE_HINT")}
										</div>
										<div className="register-table-select-wrap">
											<select
												id="sector"
												name="sector"
												defaultValue={"default"}
												className="form-control bg-transparent table-select register-table-select-input"
												onChange={handleGroupTable}
											>
												<option disabled hidden value={"default"}>
													{activeGame?.demo && activeGroup.tableNum !== ""
														? activeGroup.tableNum.toString()
														: t("REGISTER.SELECT_TABLE")}
												</option>
												{renderOptions()}
											</select>
										</div>
									</section>
								</div>
								<div className="col-12 col-lg-6 register-page-col">
									<section className="register-panel h-100">
										<div className="register-section-head">
											<p className="register-section-eyebrow">{t("REGISTER.STEP", { n: photoEnabled ? 4 : 3 })}</p>
											<h2 className="register-section-title title1 mb-2 txtBlue bold txt22">
												{t("REGISTER.TEAM_NAME")}
											</h2>
										</div>
										<div className="title1B txtBlue txt14 mb-3">{t("REGISTER.TEAM_NAME_MAX")}</div>
										<input
											id="team-full-name-table"
											autoComplete="off"
											maxLength={15}
											value={activeGroup.fullName || ""}
											onChange={handleChange("fullName")}
											placeholder={t("REGISTER.TEAM_NAME_PLACEHOLDER")}
											required
											className="form-control bg-transparent txtBlue"
										/>
									</section>
								</div>
							</div>
						</>
					)}

					<div className={`register-page-actions row mt-4 pt-2${isSimple ? " register-page-actions--simple" : ""}`}>
						<div className={`col-12 d-flex flex-wrap align-items-center ${isSimple ? "justify-content-center" : "justify-content-end"}`}>
							<button
								type="button"
								className="register-next-control"
								onClick={(e) => saveGroup(e)}
								aria-busy={loading}
								disabled={!!loading}
							>
								<span className="register-next-label">{t("COMMON.NEXT")}</span>
								<span className="register-next-icon" aria-hidden>
									<svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
										<path
											fill="none"
											stroke="currentColor"
											strokeWidth="2.4"
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M9 6l6 6-6 6"
										/>
									</svg>
								</span>
							</button>
						</div>
					</div>
				</div>
			</main>
		</div>
	);
}

export default injectIntl(Register);
